import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  Grip,
  LayerButtons,
  Notice,
  ReadonlyValue,
  RowMenu,
  SkeletonList,
  isForbidden,
  moveId,
  useAdminEditing,
} from './AdminShell'
import { createDealReason, fetchDealReasons, updateDealReason, type AdminDealReason } from './api'

/**
 * Причины закрытия сделок (тикет #100, макет AdmReasons.dc.html).
 *
 * Вид и код не меняются: причина проигрыша, ставшая причиной выигрыша, переписала бы
 * закрытые сделки, а по коду её находят интеграции. Удаления нет — только архив: закрытые
 * сделки ссылаются на причину, и отчёт за прошлый год обязан её назвать.
 *
 * Порядок в списке — порядок выбора при закрытии сделки. Перестановка сохраняется сразу:
 * причинам, чьё место изменилось, уходит новый `sortOrder` (с шагом 10, чтобы следующую
 * можно было вставить между, не трогая соседей). Числа сделок на причине справочник не
 * отдаёт — его на экране нет.
 */
type Kind = AdminDealReason['kind']
const KINDS: Kind[] = ['won', 'lost']

type Dialog = { mode: 'new'; kind: Kind } | { mode: 'rename'; reason: AdminDealReason }

const bodyOf = (reason: AdminDealReason) => ({
  kind: reason.kind,
  code: reason.code,
  name: reason.name,
  sortOrder: reason.sortOrder,
  active: reason.active,
})

export function DealReasonsPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const reasons = useQuery({ queryKey: ['admin-deal-reasons'], queryFn: ({ signal }) => fetchDealReasons(signal) })
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  const refresh = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['admin-deal-reasons'] })
    void queryClient.invalidateQueries({ queryKey: ['deal-reasons'] })
  }
  const setActive = useMutation({
    mutationFn: ({ reason, active }: { reason: AdminDealReason; active: boolean }) =>
      updateDealReason(reason.id, { ...bodyOf(reason), active }),
    onSuccess: refresh,
    onError: editing.fail,
  })
  const reorder = useMutation({
    mutationFn: async (ordered: AdminDealReason[]) => {
      for (const [index, reason] of ordered.entries()) {
        const sortOrder = (index + 1) * 10
        if (reason.sortOrder !== sortOrder) await updateDealReason(reason.id, { ...bodyOf(reason), sortOrder })
      }
    },
    onSuccess: refresh,
    onError: (error) => {
      // A half-applied order is still an order the server holds: show it, not the intended one.
      editing.fail(error)
      void queryClient.invalidateQueries({ queryKey: ['admin-deal-reasons'] })
    },
  })

  const all = reasons.data ?? []
  const forbidden = isForbidden(reasons.error)
  const activeOf = (kind: Kind) =>
    all.filter((reason) => reason.kind === kind && reason.active).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
  const move = (kind: Kind, id: string, target: number) => {
    const list = activeOf(kind)
    const ids = moveId(list.map((reason) => reason.id), id, target)
    if (ids.join() === list.map((reason) => reason.id).join()) return
    reorder.mutate(ids.map((value) => list.find((reason) => reason.id === value)!))
  }

  return (
    <AdminPage
      titleKey="adm.reasons.title"
      leadKey="adm.reasons.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.reasons.ro"
      forbidden={forbidden}
    >
      <Notice text={editing.notice} />
      <AdminState
        isLoading={reasons.isLoading}
        error={reasons.error}
        onRetry={() => void reasons.refetch()}
        forbiddenTitleKey="adm.reasons.forbidden"
        errorTitleKey="adm.reasons.error"
        skeleton={<div className="adm-two"><SkeletonList rows={5} /><SkeletonList rows={6} /></div>}
      >
        <div className="adm-two" aria-busy={reorder.isPending || undefined}>
          {KINDS.map((kind) => {
            const active = activeOf(kind)
            const archived = all.filter((reason) => reason.kind === kind && !reason.active)
            const titleId = `reasons-${kind}`
            return (
              <section key={kind} className="stack" style={{ gap: 10 }} aria-labelledby={titleId}>
                <div className="row" style={{ gap: 8, minHeight: 28 }}>
                  <span className="row" style={{ gap: 6 }}>
                    <Icon name={kind === 'won' ? 'check' : 'close'} />
                    <span className="h2" id={titleId}>{t(`adm.reasons.${kind}`)}</span>
                  </span>
                  <span className="t-sm muted">{t('adm.reasons.sub', { active: active.length, archived: archived.length })}</span>
                </div>
                {active.length === 0 && archived.length === 0 ? (
                  <StateView kind="empty" boxed compact title={t(`adm.reasons.empty.${kind}`)} description={t(`adm.reasons.empty.${kind}.text`)}
                             actions={editing.mayEdit && (
                               <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog({ mode: 'new', kind })}>
                                 <Icon name="plus" size="sm" />{t('admin.dealReason.add')}
                               </button>
                             )} />
                ) : (
                  <>
                    <div className="sort-list" role="list" aria-label={t(`adm.reasons.${kind}`)}>
                      {active.map((reason, index) => (
                        <div
                          key={reason.id}
                          role="listitem"
                          className={dragging === reason.id ? 'sort-row is-dragging' : 'sort-row'}
                          draggable={editing.mayEdit}
                          onDragStart={(event) => {
                            setDragging(reason.id)
                            event.dataTransfer?.setData('text/plain', reason.id)
                          }}
                          onDragEnd={() => setDragging(null)}
                          onDragOver={(event) => {
                            if (editing.mayEdit && dragging) event.preventDefault()
                          }}
                          onDrop={(event) => {
                            event.preventDefault()
                            const id = event.dataTransfer?.getData('text/plain') || dragging
                            setDragging(null)
                            if (id && id !== reason.id && active.some((candidate) => candidate.id === id)) move(kind, id, index)
                          }}
                        >
                          {editing.mayEdit && (
                            <Grip label={t('adm.reasons.move', { name: reason.name })} disabled={reorder.isPending}
                                  onMove={(step) => move(kind, reason.id, index + step)} />
                          )}
                          <span className="ord" style={editing.mayEdit ? undefined : { marginLeft: 8 }}>{index + 1}</span>
                          <span className="adm-grow">{reason.name}</span>
                          <span className="mono t-xs muted">{reason.code}</span>
                          {editing.mayEdit && (
                            <RowMenu label={t('adm.reasons.actions', { name: reason.name })} items={[
                              { id: 'rename', label: t('adm.reasons.rename'), icon: 'adm-edit', onSelect: () => setDialog({ mode: 'rename', reason }) },
                              {
                                id: 'archive',
                                label: t('adm.reasons.archive'),
                                icon: 'adm-archive',
                                onSelect: () => setActive.mutate({ reason, active: false }),
                              },
                            ]} />
                          )}
                        </div>
                      ))}
                      {archived.length > 0 && (
                        <div className="sort-sep"><Icon name="adm-archive" size="xs" />{t('adm.reasons.archived')}</div>
                      )}
                      {archived.map((reason) => (
                        <div key={reason.id} role="listitem" className="sort-row is-archived">
                          {editing.mayEdit ? (
                            <button type="button" className="grip" disabled aria-label={t('adm.reasons.archivedGrip')}>
                              <Icon name="adm-archive" size="sm" />
                            </button>
                          ) : (
                            <span className="ord" style={{ marginLeft: 8, borderStyle: 'dashed', background: 'transparent' }}>
                              <Icon name="adm-archive" size="xs" />
                            </span>
                          )}
                          <span className="adm-grow">{reason.name}</span>
                          <span className="mono t-xs muted">{reason.code}</span>
                          {editing.mayEdit ? (
                            <button type="button" className="btn btn-ghost btn-sm" disabled={setActive.isPending}
                                    aria-label={t('adm.reasons.restoreOf', { name: reason.name })}
                                    onClick={() => setActive.mutate({ reason, active: true })}>
                              <Icon name="refresh" size="sm" />{t('adm.reasons.restore')}
                            </button>
                          ) : (
                            <span className="badge">{t('adm.reasons.inArchive')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {editing.mayEdit && (
                      <div className="row">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog({ mode: 'new', kind })}>
                          <Icon name="plus" size="sm" />{t('admin.dealReason.add')}
                        </button>
                        <span className="t-xs muted">{t('adm.reasons.addHint')}</span>
                      </div>
                    )}
                  </>
                )}
              </section>
            )
          })}
        </div>
        {editing.mayEdit && <p className="t-sm muted">{t('adm.reasons.keyboard')}</p>}
      </AdminState>
      {dialog && (
        <ReasonModal dialog={dialog} nextOrder={dialog.mode === 'new' ? (activeOf(dialog.kind).length + 1) * 10 : 0}
                     onClose={() => setDialog(null)} onSaved={() => {
                       setDialog(null)
                       refresh()
                     }} />
      )}
    </AdminPage>
  )
}

function ReasonModal({ dialog, nextOrder, onClose, onSaved }: {
  dialog: Dialog
  nextOrder: number
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const existing = dialog.mode === 'rename' ? dialog.reason : null
  const kind = existing?.kind ?? (dialog.mode === 'new' ? dialog.kind : 'lost')
  const [code, setCode] = useState(existing?.code ?? '')
  const [name, setName] = useState(existing?.name ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () =>
      existing
        ? updateDealReason(existing.id, { ...bodyOf(existing), name })
        : createDealReason({ kind, code: code.trim(), name, sortOrder: nextOrder, active: true }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="modal"
      large={false}
      overline={t(`admin.dealReason.kind.${kind}`)}
      title={existing ? t('adm.reasons.rename') : t('admin.dealReason.add')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={existing ? t('adm.reasons.renameSave') : t('admin.dealReason.add')}
                            pending={save.isPending} disabled={name.trim() === '' || (!existing && code.trim() === '')} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.dealReason.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.dealReason.name')} htmlFor="rn-name" required
               help={existing ? t('adm.reasons.was', { name: existing.name }) : undefined}>
          <input id="rn-name" className="input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('admin.dealReason.kind')} help={existing ? t('adm.reasons.kindLocked') : undefined}>
            <ReadonlyValue>{t(`admin.dealReason.kind.${kind}`)}</ReadonlyValue>
          </Field>
          {existing ? (
            <Field label={t('admin.dealReason.code')} help={t('adm.reasons.codeLocked')}>
              <ReadonlyValue mono>{existing.code}</ReadonlyValue>
            </Field>
          ) : (
            <Field label={t('admin.dealReason.code')} htmlFor="rn-code" required help={t('adm.reasons.code.help')}>
              <input id="rn-code" className="input mono" value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
          )}
        </div>
        {existing && (
          <div className="alert alert-warning">
            <Icon name="alert" />
            <span>{t('adm.reasons.renameWarn')}</span>
          </div>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
